import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, LayoutGrid, RotateCcw, Play } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { TIERS } from "@/data/parts";
import { getProgress, progressKey, statusOf, highestDone } from "@/lib/progress";

const TONE = {
  good: "bg-emerald-500 text-slate-950 hover:bg-emerald-400",
  low: "bg-amber-400 text-slate-950 hover:bg-amber-300",
  new: "bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white",
};
const CURRENT = "ring-2 ring-indigo-400 ring-offset-2 ring-offset-slate-900 font-black";

const NumBtn = ({ n, status, current, onClick, testId, className = "" }) => (
  <button data-testid={testId} onClick={onClick} aria-current={current ? "true" : undefined} aria-label={`Question ${n}`}
    className={`grid h-9 min-w-9 place-items-center rounded-lg px-1.5 font-mono text-xs font-semibold transition-[background-color,transform] duration-150 active:scale-95 ${TONE[status]} ${current ? CURRENT : ""} ${className}`}>{n}</button>
);

export const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400" data-testid="progress-legend">
    <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />Good (70%+)</span>
    <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-amber-400" />Needs work</span>
    <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-slate-700" />Not tried</span>
    <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm ring-2 ring-indigo-400" />Current</span>
  </div>
);

// Top bar: Previous · Question [n] of N · Next · grid
export const QuestionJump = ({ idx, total, onGo, onOpenGrid }) => {
  const [val, setVal] = useState(String(idx + 1));
  useEffect(() => setVal(String(idx + 1)), [idx]);
  const commit = () => { const n = parseInt(val, 10); if (n >= 1 && n <= total) onGo(n - 1); else setVal(String(idx + 1)); };
  return (
    <div className="flex items-center gap-1 rounded-full bg-slate-800 p-1" data-testid="question-jump-bar">
      <button data-testid="practice-prev-btn" onClick={() => onGo(idx - 1)} disabled={idx === 0} className="rounded-full p-1.5 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white disabled:opacity-30" aria-label="Previous question"><ChevronLeft className="h-4 w-4" /></button>
      <label className="flex items-center gap-1.5 px-1 font-mono text-xs text-slate-400">
        <span className="hidden sm:inline">Question</span>
        <input data-testid="question-jump-input" inputMode="numeric" value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && commit()} onBlur={commit} aria-label="Jump to question"
          className="w-12 rounded-md border border-slate-600 bg-slate-900 px-1.5 py-1 text-center text-sm font-bold text-white outline-none transition-colors focus:border-indigo-400" />
        <span data-testid="question-jump-total">of {total}</span>
      </label>
      <button data-testid="practice-next-btn" onClick={() => onGo(idx + 1)} disabled={idx >= total - 1} className="rounded-full p-1.5 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white disabled:opacity-30" aria-label="Next question"><ChevronRight className="h-4 w-4" /></button>
      <button data-testid="open-question-grid-btn" onClick={onOpenGrid} className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-500" aria-label="All questions"><LayoutGrid className="h-3.5 w-3.5" /><span className="hidden sm:inline">All</span></button>
    </div>
  );
};

const pageWindow = (cur, total, span) => {
  const s = new Set([0, total - 1]);
  for (let i = cur - span; i <= cur + span; i++) if (i >= 0 && i < total) s.add(i);
  const list = [...s].sort((a, b) => a - b), out = [];
  list.forEach((v, i) => { if (i && v - list[i - 1] > 1) out.push(`gap-${v}`); out.push(v); });
  return out;
};

// Bottom website-style pager: ‹ 1 … 11 12 [13] 14 15 … 170 ›
export const QuestionPager = ({ idx, total, items, progress, onGo }) => {
  const [span, setSpan] = useState(() => (window.innerWidth < 640 ? 1 : 2));
  useEffect(() => { const h = () => setSpan(window.innerWidth < 640 ? 1 : 2); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  const arrow = "grid h-9 w-9 place-items-center rounded-lg bg-slate-800 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white disabled:opacity-30";
  return (
    <nav data-testid="question-pager" aria-label="Question pages" className="mt-6 flex flex-col items-center gap-3">
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button data-testid="pager-prev" onClick={() => onGo(idx - 1)} disabled={idx === 0} className={arrow} aria-label="Previous question"><ChevronLeft className="h-4 w-4" /></button>
        {pageWindow(idx, total, span).map((v) => typeof v === "string"
          ? <span key={v} className="px-1 font-mono text-xs text-slate-500">…</span>
          : <NumBtn key={v} n={v + 1} testId={`pager-q-${v + 1}`} status={statusOf(progress, items[v].id)} current={v === idx} onClick={() => onGo(v)} />)}
        <button data-testid="pager-next" onClick={() => onGo(idx + 1)} disabled={idx >= total - 1} className={arrow} aria-label="Next question"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <Legend />
    </nav>
  );
};

// Slide-over with one grid per level (segment)
export const QuestionGridSheet = ({ open, onOpenChange, bank, partKey, partName, tier, idx, onJump }) => {
  const [view, setView] = useState(tier);
  useEffect(() => { if (open) setView(tier); }, [open, tier]);
  const items = bank[partKey].filter((x) => (x.difficulty || "medium") === view);
  const p = getProgress(progressKey(partKey, view));
  const good = items.filter((it) => statusOf(p, it.id) === "good").length;
  const tried = items.filter((it) => it.id in p.scores).length;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="question-grid-sheet" className="flex w-full flex-col border-slate-800 bg-slate-900 p-0 text-white sm:max-w-md">
        <SheetHeader className="space-y-1 border-b border-slate-800 p-5 text-left">
          <SheetTitle className="font-display text-xl text-white">Part {partKey} · {partName}</SheetTitle>
          <SheetDescription className="text-slate-400">Tap any number to open that question. Nothing needs submitting first.</SheetDescription>
          <div className="flex rounded-full bg-slate-800 p-1 pt-1" data-testid="grid-tier-tabs">
            {TIERS.map((t) => <button key={t.key} data-testid={`grid-tier-${t.key}`} onClick={() => setView(t.key)} className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${view === t.key ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>{t.label}</button>)}
          </div>
          <div className="pt-2">
            <div className="flex justify-between font-mono text-[11px] text-slate-400"><span data-testid="grid-tried-count">{tried}/{items.length} tried</span><span data-testid="grid-good-count">{good} good</span></div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${(good / Math.max(1, items.length)) * 100}%` }} /></div>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5" data-testid={`question-grid-${view}`}>
          <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
            {items.map((it, i) => <NumBtn key={it.id} n={i + 1} testId={`grid-q-${view}-${i + 1}`} status={statusOf(p, it.id)} current={view === tier && i === idx} onClick={() => { onJump(view, i); onOpenChange(false); }} className="w-full" />)}
          </div>
        </div>
        <div className="border-t border-slate-800 p-4"><Legend /></div>
      </SheetContent>
    </Sheet>
  );
};

export const ResumeDialog = ({ resume, items, onResume, onRestart }) => {
  const p = resume ? getProgress(resume.key) : null;
  return (
    <AlertDialog open={!!resume}>
      <AlertDialogContent data-testid="resume-practice-dialog" className="max-w-[calc(100%-2rem)] rounded-2xl sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Continue from Question {resume ? resume.last + 1 : ""}?</AlertDialogTitle>
          <AlertDialogDescription data-testid="resume-practice-summary">
            {resume && `${resume.label}: you've answered ${Object.keys(p.scores).length} of ${items.length} questions (furthest: Question ${highestDone(p, items) || 1}).`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel data-testid="resume-practice-restart-btn" onClick={onRestart} className="mt-0 inline-flex items-center gap-2 rounded-full"><RotateCcw className="h-4 w-4" />Start over from Question 1</AlertDialogCancel>
          <AlertDialogAction data-testid="resume-practice-btn" onClick={onResume} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-700"><Play className="h-4 w-4" />Resume</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
